import type { CodeApproval, CodeReference, NomenclatureUpload, PendingCode, RowStatus } from "../types";

type ChipVariant = "default" | "ok" | "bad" | "info";

export const ROW_STATUS_ORDER: RowStatus[] = [
  "Ready",
  "Saved",
  "Existing",
  "Waiting",
  "Error",
  "Rejected",
  "Discarded",
];

export const ROW_STATUS_DISPLAY: Record<RowStatus, { label: string; chipVariant: ChipVariant }> = {
  Ready: { label: "Ready to save", chipVariant: "ok" },
  Saved: { label: "Saved", chipVariant: "ok" },
  Existing: { label: "Already stored", chipVariant: "default" },
  Waiting: { label: "Needs approval", chipVariant: "info" },
  Error: { label: "Fix in the file", chipVariant: "bad" },
  Rejected: { label: "Code rejected", chipVariant: "bad" },
  Discarded: { label: "Discarded", chipVariant: "default" },
};

export function rowCount(upload: NomenclatureUpload, status: RowStatus): number {
  return upload.rowCounts[status] ?? 0;
}

export function pluralRows(count: number): string {
  return `${count} ${count === 1 ? "row" : "rows"}`;
}

export function saveSummary(upload: NomenclatureUpload): string {
  const readyCount = rowCount(upload, "Ready");
  const waitingCount = rowCount(upload, "Waiting");
  const errorCount = rowCount(upload, "Error") + rowCount(upload, "Rejected");
  if (upload.uploadStatus === "Discarded") return "This upload was discarded. Nothing from it was saved.";
  if (upload.uploadStatus === "Committed") {
    return waitingCount
      ? `Saved. ${pluralRows(waitingCount)} will save automatically once their codes are approved.`
      : "Saved.";
  }
  if (!readyCount && !waitingCount) {
    return "No rows can be saved. Fix the rows marked Fix in the file and upload the file again.";
  }
  const parts = [
    readyCount ? `${pluralRows(readyCount)} will be saved now.` : "No row can be saved yet.",
    waitingCount
      ? `${pluralRows(waitingCount)} will wait for their new codes to be approved, then save automatically.`
      : "",
    errorCount ? `${pluralRows(errorCount)} with problems will be skipped.` : "",
  ];
  return parts.filter(Boolean).join(" ");
}

export function saveButtonLabel(upload: NomenclatureUpload): string {
  const readyCount = rowCount(upload, "Ready");
  if (readyCount) return `Save ${pluralRows(readyCount)}`;
  return "Save and wait for approval";
}

export function canSave(upload: NomenclatureUpload): boolean {
  return (
    upload.uploadStatus === "Review" &&
    rowCount(upload, "Ready") + rowCount(upload, "Waiting") > 0
  );
}

export function codeKey(code: CodeReference): string {
  return `${code.standardCode}|${code.slotName}|${code.code}`;
}

export function approvalWithSuggestions(pendingCode: PendingCode): CodeApproval {
  return {
    standardCode: pendingCode.standardCode,
    slotName: pendingCode.slotName,
    code: pendingCode.code,
    readsAs: (pendingCode.suggestedReadsAs ?? "").trim(),
    resinFamilyCode: pendingCode.slotName === "Base" ? (pendingCode.suggestedResinFamilyCode ?? null) : null,
  };
}

export function codesWithSuggestedReadsAs(pendingCodes: PendingCode[]): PendingCode[] {
  return pendingCodes.filter((pendingCode) => (pendingCode.suggestedReadsAs ?? "").trim() !== "");
}
